import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  CreditCard,
  Zap,
  Send,
  BarChart3,
  Receipt,
  ShieldCheck,
  Droplet,
  Signal,
  Car,
  Tv,
  Banknote,
  HeartPulse,
  Flame,
  Wallet,
  Smartphone,
  Tag,
  Building2,
  Sparkles,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/layout/AuthLayout";
import { useMagneticCursor } from "@/lib/useMagneticCursor";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import axios from "axios";

// Lucide icon name → component map. Anything outside this set falls back to Zap.
const ICON_MAP = {
  CreditCard, Send, Zap, Flame, Tv, Signal, HeartPulse, Banknote, Car,
  Droplet, Receipt, BarChart3, Wallet, Smartphone, Tag, Building2,
  Sparkles, Briefcase,
};

// Default fallback list used until /api/landing/services responds (or if the
// network call fails — we never want the landing page to render blank).
const DEFAULT_SERVICES = [
  { slug: "credit_card", label: "Credit Card", icon: "CreditCard", sub: "All banks · BBPS + native", highlight: true, kind: "bbps", target_path: "/bbps/v2?category=credit_card", login_next: "/bbps/v2?category=credit_card", utm_match: "credit_card,cc_bill,creditcard,cc" },
  { slug: "payout", label: "Payout", icon: "Send", sub: "UPI · IMPS · NEFT · RTGS", highlight: true, kind: "payout", target_path: "/payouts", login_next: "/payouts", utm_match: "payout,payouts,disbursal,disbursement,transfer" },
  { slug: "electricity", label: "Electricity", icon: "Zap", sub: "State + private DISCOMs", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=electricity", login_next: "/bbps/v2?category=electricity", utm_match: "electricity,power,bijli" },
  { slug: "gas", label: "Gas", icon: "Flame", sub: "PNG / LPG", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=gas", login_next: "/bbps/v2?category=gas", utm_match: "gas,lpg,png" },
  { slug: "dth", label: "DTH", icon: "Tv", sub: "Tata, Airtel, Sun, d2h", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=dth", login_next: "/bbps/v2?category=dth", utm_match: "dth,d2h" },
  { slug: "mobile_postpaid", label: "Mobile", icon: "Signal", sub: "Prepaid + Postpaid", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=mobile_postpaid", login_next: "/bbps/v2?category=mobile_postpaid", utm_match: "mobile,recharge,postpaid,prepaid" },
  { slug: "insurance", label: "Insurance", icon: "HeartPulse", sub: "Life + general", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=insurance", login_next: "/bbps/v2?category=insurance", utm_match: "insurance,premium,policy" },
  { slug: "loan_repayment", label: "Loan EMI", icon: "Banknote", sub: "Banks + NBFCs", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=loan_repayment", login_next: "/bbps/v2?category=loan_repayment", utm_match: "loan,emi,repayment,nbfc" },
  { slug: "fastag", label: "FASTag", icon: "Car", sub: "Top-ups, recharge", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=fastag", login_next: "/bbps/v2?category=fastag", utm_match: "fastag,toll" },
  { slug: "water", label: "Water", icon: "Droplet", sub: "Municipal boards", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=water", login_next: "/bbps/v2?category=water", utm_match: "water,municipal" },
  { slug: "broadband", label: "Broadband", icon: "Signal", sub: "ISPs pan-India", highlight: false, kind: "bbps", target_path: "/bbps/v2?category=broadband", login_next: "/bbps/v2?category=broadband", utm_match: "broadband,internet,isp,wifi" },
];

const TICKER = [
  "₹12.4Cr processed last 24h",
  "99.7% payout success rate",
  "T+0 settlements · live",
  "10+ BBPS categories",
  "UPI · IMPS · NEFT · RTGS",
  "Reconciled at transaction-level",
  "RBI-aligned audit trails",
];

// Pick the matching tile slug from the URL using ?svc=, ?service=, or any
// utm_* param against each tile's `utm_match` comma-list.
function resolveSpotlightSlug(services, search) {
  const params = new URLSearchParams(search || "");
  const direct = (params.get("svc") || params.get("service") || "").trim().toLowerCase();
  if (direct) {
    const hit = services.find((s) => s.slug === direct);
    if (hit) return hit.slug;
  }
  // Collect every utm_* value as a single haystack.
  const utmTokens = [];
  for (const [k, v] of params.entries()) {
    if (k.toLowerCase().startsWith("utm_") && v) utmTokens.push(v.toLowerCase());
  }
  const haystack = utmTokens.join(" ");
  if (!haystack) return "";
  for (const s of services) {
    const needles = (s.utm_match || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    if (needles.some((n) => haystack.includes(n))) return s.slug;
  }
  return "";
}

export default function Landing() {
  const magnetic = useMagneticCursor();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [highlightSlug, setHighlightSlug] = useState("");
  const tileRefs = useRef({});

  // Stable browser-local attribution id (used to tie post-login transactions
  // back to the original ad-click for the admin analytics funnel).
  const attributionSessionId = useMemo(() => {
    try {
      let id = localStorage.getItem("flexpay_attribution_sid");
      if (!id) {
        id = `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("flexpay_attribution_sid", id);
      }
      return id;
    } catch { return ""; }
  }, []);

  // Parse the UTM query payload once per navigation.
  const utmContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
      utm_content: params.get("utm_content") || null,
      utm_term: params.get("utm_term") || null,
      svc_param: params.get("svc") || params.get("service") || null,
      landing_path: location.pathname + location.search,
      referrer: typeof document !== "undefined" ? document.referrer : "",
    };
  }, [location.search, location.pathname]);

  // Fire a tracking event (best-effort, never throws into the UI).
  const trackEvent = (event_type, slug = null) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    try {
      axios.post(`${base}/api/landing/track`, {
        event_type,
        slug,
        attribution_session_id: attributionSessionId,
        ...utmContext,
      }).catch(() => {});
    } catch { /* swallow */ }
  };

  // Persist attribution payload for downstream conversion tracking.
  useEffect(() => {
    if (!utmContext.utm_campaign && !utmContext.utm_source && !utmContext.svc_param) return;
    try {
      localStorage.setItem("flexpay_landing_attribution", JSON.stringify({
        slug: utmContext.svc_param || "",
        ...utmContext,
        captured_at: new Date().toISOString(),
      }));
    } catch { /* ignore quota */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmContext.utm_campaign, utmContext.utm_source, utmContext.svc_param]);

  // Fetch admin-configured services (public endpoint).
  useEffect(() => {
    let cancel = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios.get(`${base}/api/landing/services`).then((res) => {
      if (cancel) return;
      // If `services` is an actual array (even empty), trust the admin — they
      // may have intentionally hidden every tile. Only fall back to defaults
      // when the API call fails entirely (handled in .catch).
      if (Array.isArray(res.data?.services)) setServices(res.data.services);
    }).catch(() => { /* keep DEFAULT_SERVICES on network error */ });
    return () => { cancel = true; };
  }, []);

  // Fire ONE view event per landing per session.
  useEffect(() => {
    const sigKey = "flexpay_landing_view_sig";
    const sig = `${location.search}|${location.pathname}`;
    try {
      if (sessionStorage.getItem(sigKey) === sig) return;
      sessionStorage.setItem(sigKey, sig);
    } catch { /* ignore */ }
    trackEvent("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  // UTM-aware spotlight: highlight + scroll-to the matching tile.
  useEffect(() => {
    const slug = resolveSpotlightSlug(services, location.search);
    if (!slug) return;
    setHighlightSlug(slug);
    // Defer scroll until tile has rendered.
    const tid = setTimeout(() => {
      const el = tileRefs.current[slug];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
    // Pulse fades after 6s (visual only — slug remains for analytics).
    const fade = setTimeout(() => setHighlightSlug(""), 6000);
    return () => { clearTimeout(tid); clearTimeout(fade); };
  }, [services, location.search]);

  const goToCategory = (svc) => {
    const target = svc?.target_path || `/bbps/v2?category=${svc?.slug || ""}`;
    const next = svc?.login_next || target;
    trackEvent("click", svc?.slug || null);
    trackEvent("lead", { service: svc?.slug || "", source: "landing_tile" });
    if (user) {
      navigate(target);
    } else {
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
  };

  // Pre-compute icon components so the render loop stays cheap.
  const enrichedServices = useMemo(
    () => services.map((s) => ({ ...s, Icon: ICON_MAP[s.icon] || Zap })),
    [services],
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <BrandMark />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" data-testid="nav-login-btn">
              <Link to="/login">Log in</Link>
            </Button>
            <Button
              asChild
              data-testid="nav-signup-btn"
              className="magnetic-btn group"
              onMouseMove={magnetic}
            >
              <Link to="/signup">
                Become a partner
                <ArrowUpRight
                  className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={1.5}
                />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Live ticker */}
      <div className="border-b border-border bg-primary text-primary-foreground overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="flex items-center">
            <div className="shrink-0 px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2 border-r border-primary-foreground/20">
              <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary pulse-dot text-secondary" />
              Live
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="marquee whitespace-nowrap py-2 text-xs">
                {[...TICKER, ...TICKER].map((t, i) => (
                  <span key={i} className="flex items-center gap-3">
                    <span className="opacity-60">◆</span>
                    <span className="font-mono">{t}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick services — moved to top so ad-clickers see CTAs first.
          Conversion-focused: compact heading + the 10 BBPS categories above the fold. */}
      <section className="relative border-b border-border bg-accent/30 overflow-hidden" data-testid="services-strip">
        <div className="absolute inset-0 dotted-pattern opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 sm:pt-10 sm:pb-12">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="overline" data-testid="services-eyebrow">Pay any bill · 10+ categories</span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1.5 leading-tight">
                Pick a service. <span className="text-secondary">Get paid in seconds.</span>
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-xs hidden sm:block">
              Credit card bills lead. Every category settles to your wallet automatically.
            </p>
          </div>

          <div className="mt-5 sm:mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4">
            {enrichedServices.map(({ slug, label, Icon, highlight, sub }) => {
              const isSpotlight = highlightSlug === slug;
              return (
              <button
                type="button"
                key={slug}
                ref={(el) => { tileRefs.current[slug] = el; }}
                data-testid={`category-${label.toLowerCase().replace(/\s+/g, "-")}`}
                data-spotlight={isSpotlight ? "true" : undefined}
                onClick={() => goToCategory({ slug, target_path: services.find((s) => s.slug === slug)?.target_path, login_next: services.find((s) => s.slug === slug)?.login_next })}
                className={[
                  "group relative surface-card p-3 sm:p-5 interactive-card cursor-pointer text-left w-full transition-all",
                  highlight ? "ring-2 ring-secondary" : "",
                  isSpotlight ? "spotlight-pulse ring-2 ring-secondary !shadow-xl scale-[1.02]" : "",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={[
                      "h-8 w-8 sm:h-10 sm:w-10 rounded-md grid place-items-center transition-all",
                      highlight
                        ? "bg-secondary text-secondary-foreground"
                        : "bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground",
                    ].join(" ")}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.5} />
                  </div>
                  <ArrowUpRight
                    className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="font-semibold mt-2 sm:mt-4 text-sm sm:text-base leading-tight">{label}</div>
                <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-snug">{sub}</div>
                {highlight && (
                  <span className="overline text-secondary mt-2 sm:mt-3 block text-[9px] sm:text-[10px]">
                    {isSpotlight ? "Picked for you" : "Primary flow"}
                  </span>
                )}
              </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-secondary/20 blur-3xl animate-float-slow pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-float-slow pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 animate-fade-up">
              <span className="overline" data-testid="hero-eyebrow">
                BBPS · Payouts · For Payment Partners
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter mt-4 leading-[0.95]">
                Process payments on <br className="hidden sm:block" />
                behalf of your consumers,
                <span className="relative inline-block ml-1 sm:ml-3">
                  <span className="relative z-10 text-secondary">without friction.</span>
                  <span className="absolute left-0 right-0 bottom-1 h-3 bg-secondary/20 -z-0 rounded" />
                </span>
              </h1>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                FlexPay is the partner-side operating system for BBPS bill payments and instant
                payouts. One dashboard for credit-card bills, utilities, UPI, IMPS, NEFT and RTGS
                disbursals — with built-in reconciliation and earnings tracking.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  data-testid="cta-signup"
                  className="magnetic-btn group relative"
                  onMouseMove={magnetic}
                >
                  <Link to="/signup">
                    Start partner onboarding
                    <ArrowUpRight
                      className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={1.5}
                    />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="cta-login">
                  <Link to="/login">I already have an account</Link>
                </Button>
              </div>

              {/* Trust row */}
              <div className="mt-10 flex items-center gap-6 flex-wrap">
                <div className="flex -space-x-2">
                  {[
                    "https://images.unsplash.com/photo-1629507208649-70919ca33793?auto=format&fit=crop&w=120&h=120&q=80",
                    "https://images.pexels.com/photos/28442317/pexels-photo-28442317.jpeg?auto=compress&cs=tinysrgb&w=120&h=120",
                    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=120&h=120&q=80",
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      className="h-9 w-9 rounded-full border-2 border-background object-cover"
                    />
                  ))}
                </div>
                <div className="text-sm">
                  <div className="font-semibold">Trusted by emerging fintech partners</div>
                  <div className="text-muted-foreground">Onboarding in under 5 minutes</div>
                </div>
              </div>
            </div>

            {/* Metrics card with animated bars */}
            <div className="lg:col-span-4">
              <div className="surface-card p-6 interactive-card relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-px shimmer" />
                <span className="overline">Partner metrics</span>
                <div className="mt-5 space-y-6">
                  {[
                    { k: "10+", v: "BBPS categories", bar: 95 },
                    { k: "4 rails", v: "UPI · IMPS · NEFT · RTGS", bar: 85 },
                    { k: "T+0", v: "Settlements", bar: 100 },
                    { k: "99.7%", v: "Payout success rate", bar: 99 },
                  ].map((r, i) => (
                    <div key={r.v}>
                      <div className="flex items-baseline justify-between gap-4">
                        <span className="font-mono text-xl">{r.k}</span>
                        <span className="text-xs text-muted-foreground text-right">{r.v}</span>
                      </div>
                      <div className="mt-2 h-1 bg-muted overflow-hidden rounded-full">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-secondary"
                          style={{
                            width: `${r.bar}%`,
                            transition: `width 1.4s cubic-bezier(.2,.9,.2,1) ${0.2 + i * 0.15}s`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banner CTA — between hero and BBPS catalogue */}
      <section className="relative overflow-hidden" data-testid="banner-cta">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div
            className="relative overflow-hidden rounded-2xl px-6 sm:px-12 py-10 sm:py-14"
            style={{
              background:
                "linear-gradient(115deg, #0a2540 0%, #1f3a8a 38%, #2563eb 72%, #38bdf8 100%)",
            }}
          >
            {/* soft grain + glow accents */}
            <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "16px 16px" }} />
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-secondary/40 blur-3xl" />
            <div className="absolute -left-32 -bottom-32 h-72 w-72 rounded-full bg-primary/40 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
              <div className="flex-1 text-white">
                <span className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Limited slots · Q2 onboarding</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 leading-tight">
                  Process <span className="text-secondary">₹50L+ a month?</span>
                  <br className="hidden sm:block" />
                  Unlock the premium partner package.
                </h2>
                <p className="text-sm sm:text-base mt-3 max-w-xl" style={{ color: "rgba(255,255,255,0.78)" }}>
                  Lower MDR, T+0 settlements, dedicated KAM, custom payout fees, and priority approval workflow.
                  Apply once — we'll get you live in under 48 hours.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:shrink-0">
                <Button
                  asChild
                  size="lg"
                  data-testid="banner-cta-primary"
                  className="magnetic-btn group bg-white text-primary hover:bg-white/90 font-semibold"
                  onMouseMove={magnetic}
                >
                  <Link to="/signup?intent=premium">
                    Apply for premium
                    <ArrowUpRight
                      className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={1.5}
                    />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  data-testid="banner-cta-secondary"
                  className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/login">I already have an account</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Send,
              title: "Payouts, your way",
              body:
                "Disburse via UPI, IMPS, NEFT or RTGS. Partners pick the rail per transaction and view the exact platform fee before confirming.",
              meta: "UPI · IMPS · NEFT · RTGS",
            },
            {
              icon: BarChart3,
              title: "Earnings, in real time",
              body:
                "Per-category commission, platform-fee configuration and price packages — surfaced on a single reports dashboard.",
              meta: "Live P&L · auto reconciled",
            },
            {
              icon: ShieldCheck,
              title: "Compliance first",
              body:
                "Mandatory KYC workflow, PAN + Aadhaar capture, and an admin console to approve onboarding partners.",
              meta: "RBI aligned · audit-ready",
            },
          ].map((f) => (
            <div key={f.title} className="group surface-card p-6 interactive-card relative">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-accent grid place-items-center text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <f.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <span className="overline">{f.meta}</span>
              </div>
              <div className="font-display text-xl font-bold mt-5">{f.title}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
              <div className="mt-5 text-sm inline-flex items-center gap-1 text-primary font-semibold opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200">
                Learn more <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden border-t border-border bg-primary text-primary-foreground">
        <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
        <div className="absolute -top-12 right-10 h-64 w-64 rounded-full bg-secondary/30 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <span className="overline text-primary-foreground/70">Ready when you are</span>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-3">
              Plug in BBPS and payouts in one afternoon.
            </h2>
            <p className="mt-4 max-w-xl text-primary-foreground/70">
              Onboard, complete KYC, and wire up your aggregator keys. Our integration layer does
              the heavy lifting so you ship to market sooner.
            </p>
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <Button
              asChild
              size="lg"
              variant="secondary"
              data-testid="bottom-cta-signup"
              className="magnetic-btn group"
              onMouseMove={magnetic}
            >
              <Link to="/signup">
                Start partner onboarding
                <ArrowUpRight
                  className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={1.5}
                />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <BrandMark />
          <div className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} FlexPay. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
